import React, { useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { BusinessCollection } from '../../data-layer/business/constants';
import {
    BusinessListItem,
    BusinessListItemSkeleton,
    calculateHeight,
} from '../../ui/business/BusinessListItem';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Input } from '../../ui/input/Input';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteList } from '../../ui/layout/InfiniteList';
import { Typography } from '../../ui/typography/Typography';
import { useTranslation } from '../../utils/useTranslation';
import { useBusinessCallbacks } from '../business/useBusinessCallbacks';
import { useBusinessCollection } from '../business/useBusinessCollection';

interface UserSubscriptionsProps {
    onClose: VoidFunction;
}

export function UserSubscriptions(props: UserSubscriptionsProps) {
    const t = useTranslation();
    const [query, setQuery] = useState('');
    const collection = useBusinessCollection({ namespace: BusinessCollection.SUBSCRIPTIONS });
    const businessCallbacks = useBusinessCallbacks();
    const filteredBusinesses = collection.businesses.filter(
        (business) => business.displayName.includes(query) || business.publicId.includes(query),
    );

    useEffectOnce(() => {
        collection.onLoadNext();
    });

    return (
        <Dialog onClose={props.onClose} height={{ xs: '100%', sm: 560 }}>
            <DialogTitle onClose={props.onClose}>
                <Typography variant="title3" weight="bold">
                    {t('Подписки')}
                </Typography>
            </DialogTitle>
            <DialogContent height="100%" py={0} px={0}>
                <Flex
                    px={{ xs: 2, sm: 3 }}
                    py={{ xs: 2, sm: 3 }}
                    direction="column"
                    alignItems="stretch"
                    width="100%"
                    minHeight="100%"
                    background="var(--background-primary)"
                    overflowY="auto"
                >
                    <Box px={0.5} pb={1}>
                        <Input
                            value={query}
                            variant="filled"
                            placeholder="поиск"
                            endAdornment={
                                query ? (
                                    <Box alignSelf="center">
                                        <IconButton
                                            size="xxs"
                                            variant="filled"
                                            onClick={() => setQuery('')}
                                        >
                                            <IconClose />
                                        </IconButton>
                                    </Box>
                                ) : undefined
                            }
                            onChange={(e) => setQuery(e.currentTarget.value)}
                        />
                    </Box>
                    {collection.isLoading || filteredBusinesses.length ? (
                        <InfiniteList
                            isEndReached
                            perPage={20}
                            isLoading={collection.isLoading}
                            count={collection.isLoading ? 5 : filteredBusinesses.length}
                            rowHeight={calculateHeight}
                            estimatedRowSize={calculateHeight}
                            renderItem={(index) => {
                                const business = filteredBusinesses[index];

                                return business ? (
                                    <BusinessListItem business={business} {...businessCallbacks} />
                                ) : (
                                    <BusinessListItemSkeleton />
                                );
                            }}
                        />
                    ) : (
                        <Flex alignItems="center" justifyContent="center" flexGrow={1}>
                            <Typography variant="body" weight="medium" color="secondary">
                                Ничего не найдено 👀
                            </Typography>
                        </Flex>
                    )}
                </Flex>
            </DialogContent>
        </Dialog>
    );
}
